import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  input,
  type OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FinoCommonModule } from '@fino-ui/common';
import { IbanPipe } from '@fino-ui/finances';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { IdentifyDocumentService } from '@modules/identify-document/identify-document.service';
import { debounce, isEmpty } from 'lodash';
import { Subject } from 'rxjs';
import { TypeaheadComponent } from '../typeahead/typeahead.component';

@Component({
  selector: 'app-iban-typeahead',
  templateUrl: './iban-typeahead.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TypeaheadComponent,
    FinoCommonModule,
    FinoIconModule,
    NgTemplateOutlet,
    DatePipe,
    IbanPipe,
  ],
})
export class IbanTypeaheadComponent implements OnInit {
  public readonly identifyDocumentService = inject(IdentifyDocumentService);

  public search = '';
  public readonly form = input<SubmittableForm>(new SubmittableForm({}));

  public readonly tenant = input<string>('');

  public readonly name = input<string>('');

  readonly disableTypeahead = input<boolean>(false);

  public readonly countryCode = input<string>('DE');

  public ibanResults = signal<any[]>([]);

  readonly openTypeahead = input<Subject<string>>(new Subject());

  @Output() ibanSelected = new EventEmitter<any>();
  ibanSelectedInternal = new EventEmitter<any>();

  @ViewChild(TypeaheadComponent) typeaheadComp: TypeaheadComponent | undefined;

  public loading = signal(false);

  counterResults = signal<any[]>([]);
  showCounterResults = signal(false);

  ibanPipe = new IbanPipe();

  searchChangedDebounced = debounce(this.searchChanged, 400);

  selectedIndex = signal(0);

  ngOnInit() {
    this.ibanSelectedInternal.subscribe((event) =>
      this.handleResultSelected(event),
    );

    this.openTypeahead().subscribe((subject: any) => {
      this.identifyDocumentService
        .search(subject.type, this.tenant(), subject.iban)
        .subscribe((r) => {
          this.counterResults.set(r);
          this.showCounterResults.set(true);
        });
    });
  }

  handleResultSelected($event: any) {
    if (!$event) return;
    this.hideResults();
    const name = this.name();
    this.form().get(name)?.setValue($event[name]);
    this.ibanSelected.next({
      iban: $event[name],
      type: name,
      customer: $event.client,
      showCounterResults: !this.showCounterResults(),
    });
    this.ibanResults.set([]);
    this.showCounterResults.set(false);
  }

  handleKeyboadSelected($event: any) {
    this.handleResultSelected(this.ibanResults()[$event]);
  }

  @HostListener('window:keydown', ['$event'])
  keyEvent($event: any) {
    if (!this.showCounterResults()) return;
    switch ($event.key) {
      case 'ArrowDown':
        this.selectedIndex.update((i) => i + 1);
        break;
      case 'ArrowUp':
        this.selectedIndex.update((i) => i - 1);
        break;
      case 'Enter':
        this.handleResultSelected(this.counterResults()[this.selectedIndex()]);
        break;
    }

    if (this.selectedIndex() < 0) {
      this.selectedIndex.set(this.counterResults().length - 1);
    }
    if (this.selectedIndex() >= this.counterResults().length) {
      this.selectedIndex.set(0);
    }
  }

  get hasValue() {
    return this.form().get(this.name())?.value;
  }

  hideResults() {
    this.typeaheadComp?.hideResults();
  }

  hideCounterResults() {
    this.showCounterResults.set(false);
  }

  searchChanged() {
    const form = this.form();
    const name = this.name();
    const searchTerm = form.get(name)?.value;

    if (!searchTerm || searchTerm.length <= 2) return;
    if (searchTerm.match(/^\d/)) {
      form.get(name)?.setValue((this.countryCode() ?? '') + searchTerm);
    }
    this.loading.set(true);
    this.identifyDocumentService
      .search(name, this.tenant(), this.countryCode(), searchTerm)
      .subscribe((r) => {
        this.loading.set(false);
        this.ibanResults.set(r);
        if (isEmpty(this.ibanResults())) {
          this.ibanResults.set([]);
        }
      });
  }

  get hasError() {
    const form = this.form();
    const name = this.name();
    return !form.get(name)?.valid && form.get(name)?.touched;
  }
}
